import { shareReplayRefCount, valuesOf } from '@adreno-common';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnDestroy,
} from '@angular/core';
import {
  FormControl,
  Validators,
  FormBuilder,
  FormGroup,
} from '@angular/forms';
import { BehaviorSubject, combineLatest, merge, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  scan,
  shareReplay,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { SubSink } from 'subsink';
import { ComboInput, ConcreteInputs, TextInput } from '../code';
import {
  isCheckInput,
  isComboInput,
  isDateInput,
  isNumberInput,
  isRadioInput,
  isTextInput,
} from '../models';

@Component({
  selector: 'app-question-input',
  templateUrl: './question-input.component.html',
  styles: [
    `
      .reduce-margin {
        margin-top: -7px;
        margin-bottom: -7px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionInputComponent implements OnDestroy {
  private _control = new BehaviorSubject<FormControl>(new FormControl());
  public get control(): FormControl {
    return this._control.value;
  }
  @Input()
  public set control(v: FormControl) {
    this._control.next(v);
  }
  private _id = new BehaviorSubject<string>('');
  @Input()
  public set id(v: string) {
    this._id.next(v);
  }
  private _input = new BehaviorSubject<ConcreteInputs>({
    type: 'text',
    label: '',
    required: false,
    size: undefined,
    visible: false,
  } as TextInput);
  @Input()
  public set input(v: ConcreteInputs) {
    this._input.next(v);
  }
  @Input()
  public set showColumnLabel(v: boolean) {
    this._showColumnLabel.next(v);
  }
  private _showColumnLabel = new BehaviorSubject<boolean>(true);

  private subs = new SubSink();

  formGroup$!: Observable<FormModel>;

  isCheckInput = isCheckInput;
  isComboInput = isComboInput;
  isDateInput = isDateInput;
  isNumberInput = isNumberInput;
  isRadioInput = isRadioInput;
  isTextInput = isTextInput;

  viewModel$!: Observable<ViewModel>;

  constructor(private fb: FormBuilder) {
    this.initViewModel();
    this.initFormControls();
  }

  private initFormControls() {
    this.formGroup$ = this._control.pipe(
      map((control) => {
        const value = Array.isArray(control.value)
          ? ((control.value || []) as string[])
          : control.value;
        if (Array.isArray(value)) {
          const otherItem = value.find((val) => val.startsWith('Other-'));
          const otherChecked = !!otherItem;
          const otherValue = otherItem?.slice(6) || '';
          return {
            control,
            otherChk: this.fb.control(otherChecked),
            otherTxt: this.fb.control(
              { value: otherValue, disabled: !otherChecked },
              Validators.required
            ),
          };
        } else if (typeof value === 'string') {
          const otherChecked = value?.startsWith('Other-') || false;
          const otherValue = (otherChecked ? value?.slice(6) : '') || '';
          return {
            control,
            otherChk: this.fb.control(otherChecked),
            otherTxt: this.fb.control(
              { value: otherValue, disabled: !otherChecked },
              Validators.required
            ),
          };
        } else {
          return {
            control,
            otherChk: this.fb.control(false),
            otherTxt: this.fb.control({ value: '', disabled: true }),
          };
        }
      }),
      shareReplayRefCount()
    );

    this.subs.sink = this.formGroup$
      .pipe(
        switchMap((fg) =>
          combineLatest([
            this._input,
            valuesOf<boolean>(fg.otherChk),
            valuesOf<string>(fg.otherTxt),
          ]).pipe(map((values) => [...values, fg] as const))
        )
      )
      .subscribe(([input, otherChk, otherTxt, fg]) => {
        const controlCtrl = fg.control;
        const otherTxtCtrl = fg.otherTxt;
        if (input.type == 'check') {
          const values = (this.control.value || []) as string[];
          const nonOtherValues = values.filter(
            (val) => !val.startsWith('Other-')
          );
          if (otherChk) {
            otherTxtCtrl.enable({ emitEvent: false });
            controlCtrl.setValue([
              ...nonOtherValues,
              `Other-${otherTxt?.trim()}`,
            ]);
          } else {
            otherTxtCtrl.disable({ emitEvent: false });
            controlCtrl.setValue(nonOtherValues);
          }
        } else if (input.type == 'radio') {
          if (otherChk) {
            otherTxtCtrl.enable({ emitEvent: false });
            controlCtrl.setValue(`Other-${otherTxt?.trim()}`);
          } else {
            otherTxtCtrl.disable({ emitEvent: false });
          }
        }
      });
  }

  private initViewModel() {
    this.viewModel$ = merge<Partial<ViewModel>>(
      this._id.pipe(map((id) => ({ id }))),
      this._input.pipe(map((input) => ({ input }))),
      this._control.pipe(
        switchMap((control) => valuesOf(control)),
        map((value) =>
          Array.isArray(value) ? ((value || []) as string[]) : (value as string)
        ),
        map((values) => {
          if (Array.isArray(values)) {
            const vmpart = {
              checked: values.reduce(
                (prev, curr) => ({ ...prev, ...{ [curr]: true } }),
                {}
              ),
            };
            return vmpart;
          } else {
            return { checked: {} };
          }
        })
      ),
      this._showColumnLabel.pipe(
        map((showColumnLabel) => ({ showColumnLabel }))
      )
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  calcComboWidth(cbo: ComboInput) {
    if (cbo.size) return `${cbo.size}em`;
    const longest = cbo.options
      .map((opt) => opt.label || opt.value)
      .reduce(
        (maxlen, label) => (maxlen > label.length ? maxlen : label.length),
        0
      );
    return `${longest * 0.55}em`;
  }

  cboAddTagFn(value: string) {
    return value;
  }

  cboOnChange(target: EventTarget | HTMLInputElement | undefined | null) {
    const checkbox = target as HTMLInputElement;
    const values = (this.control.value || []) as string[];
    if (checkbox.checked) {
      this.control.setValue([...values, checkbox.value]);
    } else {
      this.control.setValue(values.filter((v) => v !== checkbox.value));
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}

interface ViewModel {
  checked: { [key: string]: boolean };
  id: string;
  input: ConcreteInputs;
  showColumnLabel: boolean;
}

interface FormModel {
  control: FormControl;
  otherChk: FormControl;
  otherTxt: FormControl;
}
